import { Icon } from '@fourthwall/components';
import { ResetIcon, SearchIcon } from '@fourthwall/icons';
import filter from 'lodash-es/filter';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { getItemsFromProducts } from './utils/getItemsFromProducts';
import { SEARCH_MIN_LENGTH } from './consts';
import { useProductsInfiniteQuery } from './hooks/useProductsInfiniteQuery';
import { SelectProductsView } from './SelectProductsView';
import { getProductsStates } from './utils/getProductStates';
export const SelectProductsContainer = ({ multiple = true, selectMode = 'OFFERS', title, params, mode = 'COMMON', selectedIds = [], productsStateResolver, initialInputValue = '', submitButtonLabel = 'Save', cancelButtonLabel = 'Cancel', emptyMessage = 'There are no products to select.', options, onSave, close, }) => {
    const [inputSuggestion, setInputSuggestion] = useState();
    const [inputValue, setInputValue] = useState(initialInputValue);
    const [inputValueDebounced] = useDebounce(inputValue, 300);
    const [innerIds, setInnerIds] = useState(selectedIds);
    const { products, hasNextPage, fetchNextPage, isLoading } = useProductsInfiniteQuery(inputValueDebounced, options, params);
    const items = useMemo(() => getItemsFromProducts(products, selectMode), [products, selectMode]);
    const productsStates = useMemo(() => getProductsStates(productsStateResolver, products), [productsStateResolver, products]);
    const resetInputValue = useCallback(() => setInputValue(''), []);
    useEffect(() => {
        setInputSuggestion(undefined);
    }, [inputValue]);
    const handleKeyDown = useCallback((event) => {
        if (inputValue.length && inputValue.length < SEARCH_MIN_LENGTH && event.key === 'Enter') {
            setInputSuggestion(`Enter at least ${SEARCH_MIN_LENGTH} characters to start the search.`);
        }
    }, [inputValue]);
    const handleLoadMore = useCallback(() => {
        if (hasNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, fetchNextPage]);
    const handleSelect = useCallback((id) => (value) => {
        if (!multiple) {
            setInnerIds([id]);
            return;
        }
        setInnerIds((prev) => (value ? [id, ...prev] : filter(prev, (itemId) => itemId !== id)));
    }, [multiple]);
    const handleBulkSelect = useCallback((ids) => (value) => {
        if (!multiple)
            return;
        setInnerIds((prev) => value ? [...prev, ...ids] : filter(prev, (itemId) => !ids.includes(itemId)));
    }, [multiple]);
    const handleSubmitClick = useCallback(() => {
        onSave?.(innerIds);
        close();
    }, [innerIds, onSave, close]);
    const message = useMemo(() => {
        if (!multiple) {
            if (!innerIds.length)
                return 'No product selected';
            const productName = items?.find((item) => item.id === innerIds[0])?.name;
            return `${productName} selected`;
        }
        const productsCount = innerIds.length;
        return `${productsCount} ${productsCount === 1 ? 'product' : 'products'} selected`;
    }, [multiple, innerIds, items]);
    const emptyStateMessage = useMemo(() => {
        if (!products?.length) {
            return !inputValueDebounced.length ? emptyMessage : 'No products found.';
        }
        return undefined;
    }, [products, inputValueDebounced, emptyMessage]);
    const inputProps = useMemo(() => ({
        value: inputValue,
        placeholder: 'Search products',
        leftIcon: SearchIcon,
        disabled: false,
        actionIcon: inputValue.length ? React.createElement(Icon, { component: ResetIcon, height: 18 }) : undefined,
        suggestion: inputSuggestion,
        onKeyDown: handleKeyDown,
        onValueChange: setInputValue,
        onActionIconClick: resetInputValue,
    }), [inputValue, inputSuggestion, handleKeyDown, resetInputValue]);
    return (React.createElement(SelectProductsView, { title: title ?? `Select product${multiple ? 's' : ''}`, items: items, productsStates: productsStates, innerIds: innerIds, multiple: multiple, mode: mode, selectMode: selectMode, message: message, emptyMessage: emptyStateMessage, isLoading: isLoading, hasNextPage: !!hasNextPage, submitButtonLabel: submitButtonLabel, cancelButtonLabel: cancelButtonLabel, inputProps: inputProps, close: close, onLoadMore: handleLoadMore, onSelect: handleSelect, onBulkSelect: handleBulkSelect, onSubmitClick: handleSubmitClick }));
};
